// Credit System API client (contract 11 §6) — the feature's only HTTP boundary. Ported from v3
// `features/credits/api/CreditApi.ts`, re-layered onto `shared/http` (module 03): raw `appAxios`
// → `appHttp`, `{ data: T }` returns → unwrapped DTOs, and the eight `as never` casts that
// laundered the custom config field → the typed `meta` (module 03 `axios.d.ts`).
//
// User routes under `/accounts/{aId}/credits/` return the `{status,data,message}` envelope
// (`unwrapEnvelope`). The admin-panel routes this client used to also carry (flat JSON via
// `unwrap`, `/admin-panel/...`) were removed with the admin panel itself — it now lives entirely
// in a separate project, never built against this client.
//
// Every route is FEATURE-OWNED (`suppressGlobalErrorHandler`) — the credit feature renders its
// own error UX everywhere (the ambient slot degrades silently), so suppression is the class
// default, not a per-method decision.
import type { AxiosInstance, AxiosRequestConfig } from 'axios'

import type { PlansConfig } from '../models/credit.config.types'
import type {
  BalancePayload,
  CreativePreview,
  HistoryEntry,
  HistoryPayload,
  RelabelPreview,
} from '../models/credit.types'

import { appHttp, unwrapEnvelope } from '@/shared/http'

/**
 * Both flag forms, sibling parity (`createHttpClient` reads either): the request stays
 * feature-owned and a 402/403/404/409 rejects as `ApiError` to the caller instead of a toast.
 */
const FEATURE_OWNED: AxiosRequestConfig = {
  suppressGlobalErrorHandler: true,
  meta: { suppressGlobalErrorHandler: true },
}

export class CreditApi {
  constructor(private readonly http: AxiosInstance = appHttp) {}

  /* ── User-facing (enveloped) ─────────────────────────────────────────────── */

  /** §1.1 — the single read powering the topbar slot, panel, settings, lock states. */
  async getBalance(accountId: number): Promise<BalancePayload> {
    const response = await this.http.get(`/accounts/${accountId}/credits/balance/`, FEATURE_OWNED)
    return unwrapEnvelope<BalancePayload>(response)
  }

  /** §1.2 — the paginated ledger (offset/limit). */
  async getHistory(
    accountId: number,
    params?: { limit?: number; offset?: number },
  ): Promise<HistoryPayload> {
    const response = await this.http.get(`/accounts/${accountId}/credits/history/`, {
      ...FEATURE_OWNED,
      params,
    })
    const payload = unwrapEnvelope<
      Omit<HistoryPayload, 'entries'> & { results?: HistoryEntry[]; entries?: HistoryEntry[] }
    >(response)
    // Backend's canonical collection is `results`; `entries` is its temporary v3 alias. Checked
    // with `Array.isArray`, not `??`: destructuring a key absent from a plain object gives
    // `undefined`, but `payload` here is occasionally a bare array (a malformed/empty response),
    // and array destructuring resolves through the prototype chain — `entries` would silently
    // become the inherited `Array.prototype.entries` FUNCTION rather than `undefined`, which
    // `??` treats as present and never falls through to `[]`.
    const { results, entries, ...page } = payload
    return {
      ...page,
      entries: Array.isArray(results) ? results : Array.isArray(entries) ? entries : [],
    }
  }

  /**
   * §1.5 — cap-aware Creative AI cost preview for an explicit image/video count.
   * `workspace_id` optional but recommended.
   *
   * KEPT deliberately: analysis §7.2 records zero v3 call sites, but contract §6.1 retains the
   * mode and the backend serves it on the same route as {@link getCreativePreviewConfig} — it is
   * the raw-count half of one endpoint, not a dead route.
   */
  async getCreativePreview(
    accountId: number,
    params: { images: number; videos: number; workspaceId?: number | null },
  ): Promise<CreativePreview> {
    const query: Record<string, number> = { images: params.images, videos: params.videos }
    if (params.workspaceId != null) query.workspace_id = params.workspaceId
    const response = await this.http.get(`/accounts/${accountId}/credits/creative-preview/`, {
      ...FEATURE_OWNED,
      params: query,
    })
    return unwrapEnvelope<CreativePreview>(response)
  }

  /**
   * §1.5 config mode (FE-W4) — the BE resolves the run's creative count server-side from the
   * analysis config (allow-list minus already-labelled). Returns cost + a `config` block.
   */
  async getCreativePreviewConfig(
    accountId: number,
    params: {
      creative_type: 'image' | 'video'
      date_from: string
      date_to: string
      workspace_id: number
      filters?: unknown
    },
  ): Promise<CreativePreview> {
    const query: Record<string, string | number> = {
      creative_type: params.creative_type,
      date_from: params.date_from,
      date_to: params.date_to,
      workspace_id: params.workspace_id,
    }
    if (params.filters != null) query.filters = JSON.stringify(params.filters)
    const response = await this.http.get(`/accounts/${accountId}/credits/creative-preview/`, {
      ...FEATURE_OWNED,
      params: query,
    })
    return unwrapEnvelope<CreativePreview>(response)
  }

  /** §1.5 relabel mode — cost of re-labelling an existing scope. */
  async getRelabelPreview(
    accountId: number,
    params: { scope: 'image' | 'video'; workspace_id: number },
  ): Promise<RelabelPreview> {
    const response = await this.http.get(`/accounts/${accountId}/credits/creative-preview/`, {
      ...FEATURE_OWNED,
      params: { relabel: 1, scope: params.scope, workspace_id: params.workspace_id },
    })
    return unwrapEnvelope<RelabelPreview>(response)
  }

  /**
   * §2.11 — read-only credit/plan config (plans, presets, rates, default trial).
   *
   * This is a **user** route (`/accounts/{aId}/…`), not `/admin-panel/` as contract §6.2 lists,
   * and it is ENVELOPED: v3 typed the response flat and then defensively re-read `data.data`
   * through an `as any` (`useAdminCredits.ts:86-88`) — the runtime shape, not the type, is the
   * wire truth here.
   */
  async getPlansConfig(accountId: number): Promise<PlansConfig> {
    const response = await this.http.get(
      `/accounts/${accountId}/credits/plans-config/`,
      FEATURE_OWNED,
    )
    return unwrapEnvelope<PlansConfig>(response)
  }
}

export const creditApi = new CreditApi()
